
import React, { useState, useMemo } from 'react';
import { Customer, Quote, Booking, Role, Airstrip, Notification, ModuleID, AdminSettings, UserAccount, AircraftType } from '../types';
import AgentAdvisories from './AgentAdvisories';
import ScheduledCommandPanel from './ScheduledCommandPanel';
import { evaluateCustomRules, filterAdvisories, getEnforcementPolicy, getScheduledAdvisories } from '../services/advisoryRules';
import { agentService } from '../services/agent';
import { useScheduledRoutes } from '../hooks/useScheduledRoutes';
import { quoteApi } from '../services/quoteApi';
import { WhatsAppRequestHandoffPayload } from './InboxHub/whatsapp/whatsAppPanelTypes';

interface ScheduledViewProps {
  customers: Customer[];
  aircrafts: AircraftType[];
  quotes: Quote[];
  setQuotes: React.Dispatch<React.SetStateAction<Quote[]>>;
  bookings: Booking[];
  setBookings: React.Dispatch<React.SetStateAction<Booking[]>>;
  airstrips: Airstrip[];
  currentRole: Role;
  currentUser?: UserAccount | null;
  settings: AdminSettings;
  onNotify?: (notif: Omit<Notification, 'id' | 'timestamp' | 'isRead'>) => void;
  onLog?: (module: ModuleID, action: string, details: string, severity: 'LOW' | 'MEDIUM' | 'HIGH') => void;
}

const ScheduledView: React.FC<ScheduledViewProps> = ({ customers, aircrafts, quotes, setQuotes, bookings, setBookings, airstrips, currentRole, currentUser, settings, onNotify, onLog }) => {
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState('');
  const { routes: scheduledRoutes } = useScheduledRoutes();
  const [formData, setFormData] = useState({
    customerId: customers[0]?.id || '',
    from: 'ARK',
    to: 'SEU',
    date: new Date().toISOString().split('T')[0],
    paxCount: 1,
    operatorDiscount: 0,
    etd: '08:00',
    eta: '08:55',
    meetAndAssist: false,
    lounge: false
  });

  React.useEffect(() => {
    if (!isCreating) return;
    try {
      localStorage.setItem('ma_agent_context_SCHEDULED', JSON.stringify(formData));
    } catch {
      // ignore storage errors
    }
  }, [formData, isCreating]);

  React.useEffect(() => {
    const quoteId = localStorage.getItem('ma_open_quote_id');
    if (!quoteId) return;
    const found = (quotes || []).find(q => q.id === quoteId && q.productMode === 'SCHEDULED');
    if (!found) return;
    localStorage.removeItem('ma_open_quote_id');
    setIsCreating(false);
  }, [quotes]);

  React.useEffect(() => {
    const raw = localStorage.getItem('ma_whatsapp_handoff_SCHEDULED');
    if (!raw) return;
    try {
      const payload = JSON.parse(raw) as WhatsAppRequestHandoffPayload;
      setIsCreating(true);
      setCreateError('');
      setFormData(prev => ({
        ...prev,
        customerId: payload.customerId || prev.customerId,
        from: payload.fromCode || prev.from,
        to: payload.toCode || prev.to,
        date: payload.isoDate ? payload.isoDate.slice(0, 10) : prev.date,
        paxCount: payload.passengerCount || prev.paxCount,
      }));
    } catch (error) {
      console.error('Failed to restore WhatsApp scheduled handoff', error);
    } finally {
      localStorage.removeItem('ma_whatsapp_handoff_SCHEDULED');
    }
  }, []);

  const scheduledDiscountLimit = Number(currentUser?.scheduledDiscountCap ?? settings.discountCapsByRole[currentRole] ?? 0);

  const scheduledQuotes = useMemo(
    () => (Array.isArray(quotes) ? quotes.filter(q => q.productMode === 'SCHEDULED') : []),
    [quotes]
  );


  const totalRevenueUSD = useMemo(
    () => scheduledQuotes.reduce((sum, q) => sum + (Number(q.totalUSD) || 0), 0),
    [scheduledQuotes]
  );

  const totalPax = useMemo(
    () => (Array.isArray(bookings) ? bookings.reduce((sum, b) => sum + (Number(b.paxCount) || 0), 0) : 0),
    [bookings]
  );

  const activeRouteCount = useMemo(
    () => scheduledRoutes.filter(route => route.isActive !== false).length,
    [scheduledRoutes]
  );

  const completeFareRouteCount = useMemo(
    () =>
      scheduledRoutes.filter(route =>
        Number(route.rackRateUSD || 0) > 0 &&
        Number(route.rackChildUSD || 0) > 0 &&
        Number(route.basicAdultUSD || 0) > 0 &&
        Number(route.basicChildUSD || 0) > 0 &&
        Number(route.taxesUSD || 0) >= 0
      ).length,
    [scheduledRoutes]
  );

  const routeBankCoveragePct = useMemo(() => {
    if (!scheduledRoutes.length) return 0;
    return Math.round((completeFareRouteCount / scheduledRoutes.length) * 100);
  }, [scheduledRoutes, completeFareRouteCount]);

  const pricing = useMemo(() => {
    const customer = customers.find(c => c.id === formData.customerId);
    const matchingRoute = scheduledRoutes.find(r => r.from === formData.from && r.to === formData.to);
    const airstrip = airstrips.find(a => a.code === formData.to);
    
    const baseRack = matchingRoute?.rackRateUSD || 250;
    const rack = baseRack * formData.paxCount;
    const groupDiscount = customer?.categoryAssignments?.[0]?.Category?.defaultScheduleDiscount || 0;
    const customerDiscount = customer?.scheduleDiscount || 0;
    const baseDiscountPercent = customerDiscount > 0 ? customerDiscount : groupDiscount;
    const operatorDiscountPercent = Math.max(0, Math.min(formData.operatorDiscount || 0, scheduledDiscountLimit));
    const partnerDiscount = (rack * baseDiscountPercent) / 100;
    const operatorDiscount = (rack * operatorDiscountPercent) / 100;
    const discount = partnerDiscount + operatorDiscount;
    
    const maFee = formData.meetAndAssist ? (formData.paxCount * (airstrip?.meetAndAssistFee || 0)) : 0;
    const loungeFee = formData.lounge ? (formData.paxCount * (airstrip?.loungeFee || 0)) : 0;
    
    const net = rack - discount + maFee + loungeFee;
    return { rack, discount, partnerDiscount, operatorDiscount, baseDiscountPercent, operatorDiscountPercent, maFee, loungeFee, net };
  }, [formData, customers, scheduledRoutes, airstrips, scheduledDiscountLimit]);

  const advisories = useMemo(() => {
    if (!settings.agent?.enabled || !settings.agent.modules?.SCHEDULED) return [];
    if (!settings.agent.rolesByModule?.SCHEDULED?.includes(currentRole)) return [];
    if (!isCreating) return [];
    const raw = getScheduledAdvisories(formData, airstrips);
    const context = { ...formData };
    const custom = evaluateCustomRules(settings.agent?.customRulesByModule?.SCHEDULED, context);
    return filterAdvisories([...raw, ...custom], settings, 'SCHEDULED');
  }, [formData, settings, currentRole, isCreating, airstrips]);

  const enforcement = getEnforcementPolicy(settings, 'SCHEDULED');
  const shouldBlock = !settings.agent?.advisoryOnly && advisories.some(a => enforcement.blockSeverities.includes(a.severity));

  const acknowledgeAdvisories = async () => {
    if (!advisories.length) return;
    await agentService.logAudit('AGENT_ADVISORY_ACK', {
      module: 'SCHEDULED',
      count: advisories.length,
      advisories,
    });
  };

  const handleCreateBooking = async () => {
    // Final Audit (hard gate)
    if (settings.agent?.enabled && settings.agent.modules?.SCHEDULED) {
      const finalAuditSeverities = (settings.agent.enforcementByModule?.SCHEDULED || settings.agent.enforcement).blockSeverities || ['HIGH'];
      const finalAuditFail = advisories.some(a => finalAuditSeverities.includes(a.severity));
      if (finalAuditFail) {
        alert('Final Audit failed: resolve blocking advisories before issuing ticket.');
        agentService.logAudit('AGENT_FINAL_AUDIT_FAIL', { module: 'SCHEDULED', advisories });
        return;
      }
    }
    if (shouldBlock) {
      const overrideRoles = enforcement.overrideRoles || ['ADMIN', 'DFO'];
      if (overrideRoles.length > 0 && !overrideRoles.includes(currentRole)) {
        alert('Override denied: your role is not authorized to approve overrides.');
        agentService.logAudit('AGENT_OVERRIDE_DENIED', { module: 'SCHEDULED', advisories, role: currentRole });
        return;
      }
      if (enforcement.allowOverride) {
        const reason = enforcement.requireOverrideReason
          ? prompt('Override reason required to proceed:')
          : prompt('Confirm override? (Optional reason)');
        if (enforcement.requireOverrideReason && (!reason || reason.trim() === '')) {
          alert('Override cancelled. Reason is required.');
          return;
        }
        agentService.logAudit('AGENT_OVERRIDE', { module: 'SCHEDULED', advisories, reason: reason || '' });
      } else {
        alert('Blocked by Supervisor Agent: resolve required advisories first.');
        agentService.logAudit('AGENT_BLOCK', { module: 'SCHEDULED', advisories });
        return;
      }
    }
    if ((formData.operatorDiscount || 0) > scheduledDiscountLimit) {
      alert(`Operator discount exceeds your limit (${scheduledDiscountLimit}%).`);
      return;
    }
    const customer = customers.find(c => c.id === formData.customerId);
    const toIso = (day: string, hhmm: string) => {
      const safe = /^\d{2}:\d{2}$/.test(hhmm) ? hhmm : '08:00';
      return `${day}T${safe}:00.000Z`;
    };

    try {
      setCreateError('');
      const createdQuote = await quoteApi.createQuote({
        customerId: formData.customerId,
        productMode: 'SCHEDULED',
        status: 'APPROVED',
        date: formData.date,
        totalBlockTimeMinutes: 55,
        billableTimeMinutes: 55,
        discountPercentage: pricing.baseDiscountPercent + pricing.operatorDiscountPercent,
        totalUSD: pricing.net,
        costs: {
          base: pricing.rack,
          landing: 0,
          handling: 0,
          nav: 0,
          taxes: 0,
          ma: pricing.maFee,
          lounge: pricing.loungeFee,
          extraServices: 0,
          waiting: 0,
          discount: pricing.discount,
          discountBreakdown: {
            partnerPercent: pricing.baseDiscountPercent,
            issuerPercent: pricing.operatorDiscountPercent,
            partnerAmount: pricing.partnerDiscount,
            issuerAmount: pricing.operatorDiscount,
          },
        } as any,
        notes: `Scheduled ticket from Scheduled Service console (${formData.from} -> ${formData.to})`,
        legs: [
          {
            id: `LEG-${Date.now()}`,
            from: formData.from,
            to: formData.to,
            paxPick: formData.paxCount,
            paxOB: formData.paxCount,
            paxDrop: formData.paxCount,
            paxCount: formData.paxCount,
            isDeadLeg: false,
            distance: 0,
            blockTimeMinutes: 55,
            etd: toIso(formData.date, formData.etd),
            eta: toIso(formData.date, formData.eta),
          },
        ],
      });

      const firstLeg = createdQuote.legs?.[0];
      const newBooking: Booking = {
        id: createdQuote.bookingId || `B-${createdQuote.id}`,
        customerId: createdQuote.customerId,
        customerName: createdQuote.customerName || customer?.name || 'Unknown',
        date: createdQuote.date || formData.date,
        paxCount: firstLeg?.paxCount || formData.paxCount,
        from: firstLeg?.from || formData.from,
        to: firstLeg?.to || formData.to,
        quoteId: createdQuote.id,
      };

      setQuotes(prev => [createdQuote, ...prev.filter(q => q.id !== createdQuote.id)]);
      setBookings(prev => [newBooking, ...prev.filter(b => b.quoteId !== createdQuote.id)]);
      setIsCreating(false);

      onNotify?.({
        title: 'Ticket Issued',
        message: `${createdQuote.quoteNumber || createdQuote.id} confirmed for ${newBooking.customerName}`,
        type: 'SUCCESS',
      });
      onLog?.('SCHEDULED', 'CREATE', `Seat booking ${newBooking.id} confirmed`, 'LOW');
    } catch (error: any) {
      setCreateError(error?.response?.data?.error || error?.message || 'Failed to create scheduled ticket');
    }
  };

  return (
    <div className="max-w-7xl mx-auto space-y-12 animate-in fade-in pb-24">
      {!isCreating ? (
         <div className="space-y-10">
            <div className="relative overflow-hidden rounded-[2.5rem] border border-indigo-100 bg-[linear-gradient(130deg,#0f172a_0%,#1e293b_42%,#0b3a68_100%)] p-10 shadow-[0_25px_60px_rgba(15,23,42,0.28)]">
               <div className="pointer-events-none absolute -top-16 -right-10 h-52 w-52 rounded-full bg-cyan-300/20 blur-3xl" />
               <div className="pointer-events-none absolute -bottom-20 left-1/3 h-56 w-56 rounded-full bg-indigo-400/20 blur-3xl" />
               <div className="relative flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
                 <div>
                   <p className="text-[10px] font-black uppercase tracking-[0.35em] text-cyan-200/80">Scheduled Command</p>
                   <h2 className="mt-2 text-4xl font-black tracking-tight text-white">Scheduled Services</h2>
                   <p className="mt-3 text-sm font-semibold text-slate-200/90">Consolidation, ticket issuance, and seat-yield control in one board.</p>
                 </div>
                 <button onClick={() => setIsCreating(true)} className="rounded-2xl border border-cyan-200/40 bg-cyan-400/90 px-8 py-4 text-[11px] font-black uppercase tracking-[0.2em] text-slate-950 shadow-xl shadow-cyan-900/20 transition-all hover:bg-cyan-300">
                   Issue Individual Ticket
                 </button>
               </div>
               <div className="relative mt-8 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-6">
                 <div className="rounded-2xl border border-white/15 bg-white/10 px-5 py-4 backdrop-blur">
                   <p className="text-[10px] font-black uppercase tracking-[0.25em] text-slate-300">Scheduled Quotes</p>
                   <p className="mt-2 text-3xl font-black text-white">{scheduledQuotes.length}</p>
                 </div>
                 <div className="rounded-2xl border border-white/15 bg-white/10 px-5 py-4 backdrop-blur">
                   <p className="text-[10px] font-black uppercase tracking-[0.25em] text-slate-300">Bookings</p>
                   <p className="mt-2 text-3xl font-black text-white">{Array.isArray(bookings) ? bookings.length : 0}</p>
                 </div>
                 <div className="rounded-2xl border border-white/15 bg-white/10 px-5 py-4 backdrop-blur">
                   <p className="text-[10px] font-black uppercase tracking-[0.25em] text-slate-300">Passenger Volume</p>
                   <p className="mt-2 text-3xl font-black text-white">{totalPax}</p>
                 </div>
                 <div className="rounded-2xl border border-white/15 bg-white/10 px-5 py-4 backdrop-blur">
                   <p className="text-[10px] font-black uppercase tracking-[0.25em] text-slate-300">Revenue (USD)</p>
                   <p className="mt-2 text-3xl font-black text-white">${Math.round(totalRevenueUSD).toLocaleString()}</p>
                 </div>
                 <div className="rounded-2xl border border-white/15 bg-white/10 px-5 py-4 backdrop-blur">
                   <p className="text-[10px] font-black uppercase tracking-[0.25em] text-slate-300">Active Routes</p>
                   <p className="mt-2 text-3xl font-black text-white">{activeRouteCount}</p>
                 </div>
                 <div className="rounded-2xl border border-white/15 bg-white/10 px-5 py-4 backdrop-blur">
                   <p className="text-[10px] font-black uppercase tracking-[0.25em] text-slate-300">Route Bank Coverage</p>
                   <p className="mt-2 text-3xl font-black text-white">{routeBankCoveragePct}%</p>
                 </div>
               </div>
            </div>

            <div className="rounded-[2rem] border border-slate-200 bg-white p-5 shadow-[0_10px_30px_rgba(15,23,42,0.06)]">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-[10px] font-black uppercase tracking-[0.25em] text-slate-500">Pricing Source</p>
                  <h3 className="mt-1 text-xl font-black tracking-tight text-slate-900">Route Bank Driven Scheduled Pricing</h3>
                  <p className="mt-1 text-sm font-semibold text-slate-600">
                    Seat pricing is pulled from System Registry Routes. Operators only offer, escalate, or approve.
                  </p>
                </div>
                <span className={`inline-flex items-center rounded-full border px-3 py-1 text-[10px] font-black uppercase tracking-[0.15em] ${
                  routeBankCoveragePct >= 90 ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : routeBankCoveragePct >= 60 ? 'border-amber-200 bg-amber-50 text-amber-700' : 'border-rose-200 bg-rose-50 text-rose-700'
                }`}>
                  Coverage {routeBankCoveragePct}%
                </span>
              </div>
            </div>

            <ScheduledCommandPanel
              currentUser={currentUser ? { id: currentUser.id, name: currentUser.name, email: currentUser.email, role: currentUser.role } : null}
              routes={scheduledRoutes}
              aircrafts={aircrafts}
              customers={customers}
              operatorDiscountCap={scheduledDiscountLimit}
              onNotify={(title, message, type) => {
                onNotify?.({ title, message, type });
              }}
            />

         </div>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-[1fr_360px] gap-8">
        <div className="bg-white rounded-[4rem] p-12 shadow-2xl border border-slate-100">
           <div className="flex justify-between items-start mb-16">
              <div>
                <h3 className="text-4xl font-black text-slate-900 tracking-tighter uppercase leading-none">Seat Reservation Node</h3>
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mt-4">Authorized Channel: Scheduled Service</p>
              </div>
              <button onClick={() => setIsCreating(false)} className="w-14 h-14 rounded-2xl bg-slate-50 text-slate-400 hover:bg-rose-500 hover:text-white transition-all flex items-center justify-center">
                 <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
           </div>

           <div className="grid grid-cols-1 lg:grid-cols-3 gap-16">
              <div className="lg:col-span-2 space-y-12">
                 <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                    <div className="space-y-4">
                       <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2">Commercial Partner</label>
                       <select className="w-full bg-slate-50 border-none rounded-2xl p-6 font-bold text-slate-800 outline-none appearance-none" value={formData.customerId} onChange={e => setFormData({...formData, customerId: e.target.value})}>
                          {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                       </select>
                    </div>
                    <div className="space-y-4">
                       <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2">Flight Date</label>
                       <input type="date" className="w-full bg-slate-50 border-none rounded-2xl p-6 font-bold text-slate-800 outline-none" value={formData.date} onChange={e => setFormData({...formData, date: e.target.value})} />
                    </div>
                    <div className="space-y-4">
                       <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2">Service Route</label>
                       <select className="w-full bg-slate-50 border-none rounded-2xl p-6 font-bold text-slate-800 outline-none appearance-none" value={`${formData.from}|${formData.to}`} onChange={e => {
                            const [from, to] = e.target.value.split('|');
                            setFormData({...formData, from, to});
                         }}>
                          {scheduledRoutes.map(r => <option key={r.id} value={`${r.from}|${r.to}`}>{r.from} ➔ {r.to}</option>)}
                       </select>
                    </div>
                    <div className="space-y-4">
                       <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2">Total PAX</label>
                       <input type="number" min="1" className="w-full bg-slate-50 border-none rounded-2xl p-6 font-bold text-slate-800 outline-none" value={formData.paxCount} onChange={e => setFormData({...formData, paxCount: parseInt(e.target.value) || 1})} />
                    </div>
                 </div>
              </div>
              <div className="space-y-8">
                 <div className="bg-slate-900 rounded-[3.5rem] p-12 text-white shadow-2xl space-y-10 sticky top-10 overflow-hidden">
                    <h4 className="text-[10px] font-black uppercase tracking-[0.4em] text-indigo-400 pb-6 border-b border-white/10">Commercial Synthesis</h4>
                    <div className="space-y-5">
                       <div className="flex justify-between text-[11px] font-bold text-slate-500 uppercase tracking-widest"><span>Rack Subtotal</span><span>${pricing.rack.toLocaleString()}</span></div>
                       <div className="flex justify-between text-[11px] font-bold text-emerald-400 uppercase tracking-widest"><span>Partner Rebate</span><span>-${Math.round(pricing.partnerDiscount).toLocaleString()}</span></div>
                       <div className="space-y-2 rounded-xl border border-white/10 bg-white/5 p-3">
                         <div className="flex items-center justify-between text-[9px] font-black uppercase tracking-widest text-slate-300">
                           <span>Operator Discount %</span>
                           <span className="text-cyan-300">Limit {scheduledDiscountLimit}%</span>
                         </div>
                         <input
                           type="number"
                           min="0"
                           max={scheduledDiscountLimit}
                           step="0.1"
                           className="w-full rounded-lg border border-white/20 bg-slate-900/60 px-3 py-2 text-sm font-bold text-white outline-none"
                           value={formData.operatorDiscount}
                           onChange={e => setFormData({ ...formData, operatorDiscount: Math.max(0, Number(e.target.value) || 0) })}
                         />
                         <div className="flex justify-between text-[10px] text-slate-300">
                           <span>Applied</span>
                           <span>-${Math.round(pricing.operatorDiscount).toLocaleString()}</span>
                         </div>
                       </div>
                       <div className="pt-10 border-t border-white/10">
                          <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-2">Invoice Value</p>
                          <p className="text-6xl font-black font-mono tracking-tighter text-indigo-400">${Math.round(pricing.net).toLocaleString()}</p>
                       </div>
                    </div>
                    {createError && (
                      <p className="text-[10px] font-black uppercase tracking-widest text-rose-300">{createError}</p>
                    )}
                    <button onClick={handleCreateBooking} className="w-full py-8 bg-indigo-600 text-white rounded-3xl font-black text-[11px] uppercase tracking-[0.3em] hover:bg-indigo-500 transition-all shadow-xl shadow-indigo-900/50">Issue Secure Ticket</button>
                 </div>
              </div>
           </div>
        </div>
        {settings.agent?.enabled && settings.agent.modules?.SCHEDULED && settings.agent.rolesByModule?.SCHEDULED?.includes(currentRole) && (
          <AgentAdvisories
            advisories={advisories}
            mode={settings.agent.advisoryMode}
            moduleLabel="Scheduled"
            onAcknowledge={acknowledgeAdvisories}
            showEmptyState
          />
        )}
        </div>
      )}
    </div>
  );
};

export default ScheduledView;
